<template>
  <div class="settings-page">
    <div class="page-header">
      <div>
        <h2>Admin Settings</h2>
        <p>Manage your administrator account security.</p>
      </div>
    </div>

    <div class="settings-card">
      <div class="card-heading">
        <div class="heading-icon">
          <i class="bx bx-lock-alt"></i>
        </div>
        <div>
          <h3>Change Password</h3>
          <p>Use a strong password that you do not reuse elsewhere.</p>
        </div>
      </div>

      <div class="form-grid">
        <div class="form-group full">
          <label for="current-password">Current Password</label>
          <input
            id="current-password"
            v-model="form.currentPassword"
            type="password"
            autocomplete="current-password"
            placeholder="Enter current password"
          />
        </div>

        <div class="form-group">
          <label for="new-password">New Password</label>
          <input
            id="new-password"
            v-model="form.newPassword"
            type="password"
            autocomplete="new-password"
            placeholder="Minimum 8 characters"
          />
        </div>

        <div class="form-group">
          <label for="confirm-password">Confirm New Password</label>
          <input
            id="confirm-password"
            v-model="form.confirmPassword"
            type="password"
            autocomplete="new-password"
            placeholder="Re-enter new password"
          />
        </div>
      </div>

      <div class="form-actions">
        <button class="btn-secondary" @click="resetForm">Clear</button>
        <button class="btn-primary" :disabled="saving" @click="changePassword">
          {{ saving ? "Updating..." : "Update Password" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { changeAdminPasswordApi } from "@/apis/adminprofileapis";

export default {
  name: "AdminSettingsScreen",

  data() {
    return {
      saving: false,
      form: {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      },
    };
  },

  methods: {
    resetForm() {
      this.form = {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      };
    },

    async changePassword() {
      const currentPassword = this.form.currentPassword.trim();
      const newPassword = this.form.newPassword;
      const confirmPassword = this.form.confirmPassword;

      if (!currentPassword || !newPassword || !confirmPassword) {
        alert("All password fields are required.");
        return;
      }

      if (newPassword.length < 8) {
        alert("New password must be at least 8 characters.");
        return;
      }

      if (newPassword === currentPassword) {
        alert("New password must be different from the current password.");
        return;
      }

      if (newPassword !== confirmPassword) {
        alert("New password and confirmation do not match.");
        return;
      }

      this.saving = true;

      try {
        const response = await changeAdminPasswordApi({
          currentPassword,
          newPassword,
        });

        if (!response?.status) {
          alert(response?.success || "Unable to change password.");
          return;
        }

        this.resetForm();
        alert("Password changed successfully. Please sign in again.");
        sessionStorage.removeItem("ldata");
        sessionStorage.removeItem("accessToken");
        this.$router.push("/login");
      } catch (error) {
        alert("Unable to change password.");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.settings-page {
  min-height: 100vh;
  padding: 28px;
  box-sizing: border-box;
  background: #f8fafc;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #0f172a;
  font-size: 24px;
}

.page-header p {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 12px;
}

.settings-card {
  width: 100%;
  max-width: 720px;
  padding: 24px;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 15px;
  background: #ffffff;
}

.card-heading {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid #f1f5f9;
}

.heading-icon {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 21px;
}

.card-heading h3 {
  margin: 0;
  color: #0f172a;
  font-size: 16px;
}

.card-heading p {
  margin: 4px 0 0;
  color: #94a3b8;
  font-size: 11px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group.full {
  grid-column: 1 / -1;
}

.form-group label {
  color: #475569;
  font-size: 11px;
  font-weight: 700;
}

.form-group input {
  height: 40px;
  padding: 0 11px;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #334155;
  outline: none;
  font-size: 12px;
}

.form-group input:focus {
  border-color: #2563eb;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 9px;
  padding-top: 20px;
  margin-top: 20px;
  border-top: 1px solid #f1f5f9;
}

.btn-primary,
.btn-secondary {
  height: 40px;
  padding: 0 15px;
  border-radius: 9px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.btn-primary {
  border: none;
  background: #2563eb;
  color: #ffffff;
}

.btn-primary:disabled {
  opacity: .6;
  cursor: not-allowed;
}

.btn-secondary {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #334155;
}

@media (max-width: 600px) {
  .settings-page {
    padding: 16px;
  }

  .settings-card {
    padding: 18px;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-group.full {
    grid-column: auto;
  }

  .form-actions {
    flex-direction: column-reverse;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
  }
}
</style>
